import cx from "classnames";
import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { ScheduleItem } from "src/types";

import useProperties from "@/hooks/use-properties";
import formatLocalDatetimeString from "@/utils/dates/formatLocalDatetimeString";
import endpoints from "@/utils/endpoints";

import { AddToCalendarWrapper } from "./add-to-calendar-component-styles";
export type EventItem = Pick<
  ScheduleItem,
  "external_id" | "name" | "time_end" | "time_start"
>;

type AddToCalendarProps = {
  children: React.ReactNode;
  event: EventItem;
  className?: string;
};

const get = endpoints();
const ISO_WITH_TIME = "yyyy-MM-dd'T'HH:mm'Z'";
const CALENDAR_OPTION = ".addeventatc_dropdown span[class^='ate']";
const ADD_EVENT_LINK = ".addeventatc_dropdown .frs a";

const AddToCalendar = (props: AddToCalendarProps) => {
  const { t } = useTranslation();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { time_start, time_end, name, external_id } = props.event;

  const renderedCalendarProperties = useMemo(() => {
    const calendarProperties = {
      description: `${get("HOPIN_ROOT")}/cal/${external_id}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      location: `${t("schedule.hosted-virtually-on-events")}`,
      start: formatLocalDatetimeString(time_start, ISO_WITH_TIME),
      end: formatLocalDatetimeString(time_end, ISO_WITH_TIME),
      title: name,
      client: "arcVMTbPfzDtviwEomMj51030",
    };

    return Object.entries(calendarProperties).map(([key, value]) => (
      <span key={key} className={key}>
        {value}
      </span>
    ));
  }, [external_id, t, time_start, time_end, name]);

  const colors = useProperties();
  useEffect(() => {
    if (window.addeventatc) {
      window.addeventatc.refresh();
    }
  }, [colors]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const makeOptionsFocusable = () => {
      wrapper
        .querySelectorAll<HTMLElement>(`${CALENDAR_OPTION}, ${ADD_EVENT_LINK}`)
        .forEach((option) => {
          option.tabIndex = 0;
          if (option.matches(CALENDAR_OPTION) || !option.hasAttribute("href")) {
            option.setAttribute("role", "button");
          } else if (option.getAttribute("role") === "button") {
            option.removeAttribute("role");
          }
        });
    };

    const observer = new MutationObserver(makeOptionsFocusable);
    observer.observe(wrapper, {
      attributes: true,
      attributeFilter: ["href"],
      childList: true,
      subtree: true,
    });
    makeOptionsFocusable();

    return () => observer.disconnect();
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const isCalendarOption = target.matches(CALENDAR_OPTION);
    const isFooterLinkWithoutHref =
      target.matches(ADD_EVENT_LINK) && !target.hasAttribute("href");

    if (
      (isCalendarOption || isFooterLinkWithoutHref) &&
      (event.key === "Enter" || event.key === " ")
    ) {
      event.preventDefault();
      target.click();
    }
  };

  return (
    <AddToCalendarWrapper
      ref={wrapperRef}
      className={cx("addeventatc", props.className)}
      data-styling="none"
      data-testid="addeventatc-wrapper"
      onKeyDown={handleKeyDown}
    >
      {props.children}
      {renderedCalendarProperties}
    </AddToCalendarWrapper>
  );
};

export default AddToCalendar;
